import {
  calculateConceptTotals,
  unitPriceForSubtotal,
  type ConceptRetentionRates,
  type ConceptTotals,
} from '@/shared/fiscal/conceptTotals';
import type { Receiver } from '@/shared/services/invoice.service';
import type { IvaTreatment } from '../../constants/ivaTreatment';
import { displayFiscalName } from '../../lib/displayName';
import { foreignTaxIdProblem, normalizeForeignTaxId } from '../../lib/foreignReceiver';
import { periodicityOptionsFor, type GlobalInformation } from '../../lib/informacionGlobal';
import { inferIsGoods, receiverCanWithhold } from '../../lib/retentions';
import {
  allowedUsosForRegime,
  getDefaultUsoCfdiForRegime,
  USOS_CFDI,
  type SatCatalogItem,
} from '../../constants/satCatalogs';
import { stripCodePrefix } from './search';

/**
 * Borrador de factura del flujo rediseñado.
 *
 * Contrato: es el único estado editable del flujo. Todo lo que se puede derivar
 * —importes, uso de CFDI, retenciones, si se puede timbrar— se calcula con las
 * funciones de este archivo y NUNCA se guarda como campo, para que un borrador
 * restaurado no pueda mostrar un total que ya no corresponde a sus conceptos.
 */

/**
 * Los tipos de comprobante que ESTE flujo emite. Hoy solo ingreso.
 *
 * El complemento de pago (P) se quedó fuera a propósito y no como pendiente
 * sin anotar: vive en el flujo legacy, que ya lleva el libro de abonos
 * (`invoice_payment`) y deja que el servidor derive parcialidad, saldos e
 * impuestos. En el fiscal-engine el P se genera pero su emisión sigue
 * rechazada hasta la fase 2, y entra por el mismo endpoint que el ingreso: un P
 * que saliera de aquí iría a un motor que lo rechaza con un contrato que ya no
 * existe. Mejor que el tipo no lo pueda expresar a que una validación lo tenga
 * que atrapar.
 */
export type DraftInvoiceKind = 'ingreso';

/**
 * `Record` sobre el tipo, y no un ternario: agregar un tipo de comprobante
 * (el egreso es el siguiente que el fiscal-engine ya soporta) truena aquí en
 * compilación hasta que alguien decida cómo se timbra.
 */
export const INVOICE_TYPE_BY_KIND: Record<DraftInvoiceKind, 'I'> = {
  ingreso: 'I',
};

export type ReceiverMode = 'publico_general' | 'publico_extranjero' | 'cliente';

/** RFC genérico del SAT para operaciones con público en general. */
export const PUBLIC_RECEIVER_RFC = 'XAXX010101000';
/** RFC genérico del SAT para clientes residentes en el extranjero, sin RFC mexicano. */
export const FOREIGN_RECEIVER_RFC = 'XEXX010101000';
/** Régimen «Sin obligaciones fiscales», el que exige el receptor genérico. */
export const PUBLIC_RECEIVER_REGIME = '616';
/** Uso de CFDI «Sin efectos fiscales», derivado de facturar a público en general. */
export const PUBLIC_RECEIVER_USO_CFDI = 'S01';

export interface DraftReceiver {
  mode: ReceiverMode;
  clientId?: number;
  name: string;
  rfc: string;
  taxRegime: string;
  postalCode: string;
  email?: string;
  /**
   * Uso de CFDI que eligió el usuario para este receptor. Vive en el receptor y
   * no en el borrador para que cambiar de cliente no arrastre el uso del
   * anterior. Se valida contra el régimen en `resolveUsoCfdi`, nunca se usa
   * crudo.
   */
  usoCfdi?: string;
  /**
   * Periodo de la factura global. Solo viaja con Público en general: sin él, el
   * nodo no se manda y el PAC lo llena con periodicidad diaria y el mes y año de
   * la fecha de emisión.
   */
  globalInformation?: GlobalInformation;
  /** País de residencia fiscal (c_Pais) de un cliente en el extranjero. Opcional. */
  residenciaFiscal?: string;
  /** Su número de registro fiscal en ese país. Opcional, pero con él el SAT exige el país. */
  numRegIdTrib?: string;
}

export interface DraftIssuer {
  name: string;
  rfc: string;
  taxRegime: string;
  taxRegimeName?: string;
  postalCode: string;
  /**
   * Correo de la cuenta. No es un destinatario que el usuario elige: el backend
   * lo compara contra cada dirección para decidir a quién le toca la plantilla
   * de emisor y a quién la de cliente. Sin él, el emisor no recibe su copia.
   */
  email?: string;
}

export interface DraftConcept {
  id: string;
  productId?: number;
  satCode: string;
  /** Nombre del producto tal como viene del catálogo del SAT. */
  description: string;
  /** Detalle opcional que el emisor agrega para su cliente. */
  detail?: string;
  unitCode: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  ivaTreatment: IvaTreatment;
  /** El precio capturado es lo que el emisor quiere recibir, no la base. */
  grossUp: boolean;
  saveProduct: boolean;
  /**
   * Derivadas, no capturadas: las pone `withRetentions` con la regla del
   * régimen del emisor frente a este receptor y las tasas que capturó el
   * usuario. Cualquier valor guardado se sobrescribe al derivar.
   */
  retentions?: ConceptRetentionRates;
}

/** Transferencia electrónica: lo que se precarga en una factura nueva. */
export const DEFAULT_PAYMENT_FORM = '03';
/** «Por definir». La única forma de pago que el SAT admite en una factura PPD. */
export const PPD_PAYMENT_FORM = '99';

export interface DraftPayment {
  /** PUE = una sola exhibición · PPD = en parcialidades. */
  method: 'PUE' | 'PPD';
  /** Clave c_FormaPago del SAT. `03` = transferencia electrónica. */
  form: string;
  currency: 'MXN' | 'USD' | 'EUR';
  exchangeRate?: number;
  /** Cuándo recibió el pago (`AAAA-MM-DD`): la fecha con la que se busca el tipo de cambio oficial. */
  receivedOn?: string;
  /** `official` = prellenado del DOF; `manual` = lo capturó el usuario y un cambio de fecha no lo pisa. */
  exchangeRateSource?: 'official' | 'manual';
}

export interface InvoiceDraft {
  id: string;
  kind: DraftInvoiceKind;
  receiver: DraftReceiver | null;
  issuer: DraftIssuer | null;
  concepts: DraftConcept[];
  payment: DraftPayment;
  emails: string[];
  /**
   * Fecha de emisión que eligió el usuario, hasta 72 h atrás. Vacía es «hoy, al
   * timbrar». Caduca: la ventana y sus reglas viven en `emissionDate.ts`.
   */
  backdatedTo?: string;
  /**
   * Retenciones de la regla que el usuario quitó porque su cliente no se las hace.
   * Junto con `retentionRates`, las únicas excepciones CAPTURADAS a algo derivado:
   * ver `RetentionWaiver`.
   */
  retentionWaiver?: RetentionWaiver;
  /** Las tasas que el usuario capturó porque su cliente le retiene otra cosa: ver `RetentionRates`. */
  retentionRates?: RetentionRates;
  updatedAt: string;
}

/**
 * «Mi cliente no me retiene»: las retenciones que la regla de Taxes pone y que el
 * usuario quita a mano, cada una por separado.
 *
 * Existe porque la regla dice lo que el cliente DEBE retener, no lo que retiene: hay
 * personas morales que pagan completo y piden la factura sin retenciones (o solo con
 * la de ISR), y el SAT la timbra igual. Sin esta salida el emisor se va a timbrar a
 * otro lado. Retener es obligación del receptor; si no lo hace, el emisor entera él
 * esos impuestos en su declaración.
 *
 * `scope` ata la decisión al régimen del emisor y al RFC del receptor para los que se
 * tomó. Si cambia cualquiera de los dos deja de aplicar y la regla vuelve a mandar:
 * quitar retenciones es una excepción por cliente, nunca un default que se arrastre a
 * otro receptor. Por lo mismo no se copia al repetir una factura.
 */
export interface RetentionWaiver {
  isr: boolean;
  iva: boolean;
  scope: string;
}

/**
 * «Mi cliente me retiene otra tasa»: la tasa de cada impuesto tal como la capturó
 * el usuario, en decimal y a 6 decimales (0.025 = 2.5%).
 *
 * Existe porque el catálogo de Taxes solo cubre 626, 612 y 606: un emisor de otro
 * régimen al que una persona moral sí le retiene (la plataforma con la que trabaja
 * un 625) no tenía cómo ponerlo en su factura, y con regla la tasa real puede no
 * ser la del catálogo.
 *
 * Por impuesto: ausente = manda la regla (o no se retiene si no hay regla); `null` =
 * el usuario borró la tasa y falta capturarla, lo que bloquea el timbrado en vez de
 * mandar la factura sin esa retención; número = esa tasa.
 *
 * Mismo `scope` que `RetentionWaiver`: es de este emisor con este receptor. A
 * diferencia de la excepción, sí se copia al repetir una factura: es lo que ese
 * cliente retiene, y el `scope` la apaga si cambia el cliente o el régimen.
 */
export interface RetentionRates {
  isr?: number | null;
  iva?: number | null;
  scope: string;
}

export type RetentionTax = 'isr' | 'iva';

export const RETENTION_TAXES: readonly RetentionTax[] = ['isr', 'iva'];

export const RETENTION_TAX_NAME: Record<RetentionTax, string> = { isr: 'ISR', iva: 'IVA' };

const RATE_KEY = {
  isr: 'isrRate',
  iva: 'ivaRate',
} as const satisfies Record<RetentionTax, keyof ConceptRetentionRates>;

/** La tasa de ese impuesto en el concepto ya derivado, o 0. */
export const conceptRetentionRateOf = (concept: DraftConcept, tax: RetentionTax): number =>
  concept.retentions?.[RATE_KEY[tax]] ?? 0;

const IVA_RATE_BY_TREATMENT: Record<IvaTreatment, number> = {
  tasa_16: 0.16,
  tasa_8: 0.08,
  tasa_0: 0,
  exento: 0,
  no_objeto: 0,
};

export const ivaRateOf = (treatment: IvaTreatment): number =>
  IVA_RATE_BY_TREATMENT[treatment] ?? 0;

export const createEmptyConcept = (): DraftConcept => ({
  id: crypto.randomUUID(),
  satCode: '',
  description: '',
  unitCode: 'E48',
  unitName: 'Servicio',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  ivaTreatment: 'tasa_16',
  grossUp: true,
  saveProduct: false,
});

export const createEmptyDraft = (issuer: DraftIssuer | null): InvoiceDraft => ({
  id: crypto.randomUUID(),
  kind: 'ingreso',
  receiver: null,
  issuer,
  concepts: [createEmptyConcept()],
  payment: { method: 'PUE', form: DEFAULT_PAYMENT_FORM, currency: 'MXN' },
  emails: [],
  updatedAt: new Date().toISOString(),
});

/**
 * Las reglas del pago que el SAT impone y que el usuario no decide.
 *
 * Una factura PPD («pago en parcialidades») tiene que llevar forma de pago 99:
 * todavía no se sabe cómo le van a pagar. Ningún backend lo corrige —ni el Go
 * ni el NestJS—, así que si esto no se sostiene aquí el comprobante sale con 03
 * y el SAT lo rechaza. Y pega más allá de esta factura: toda PPD es la factura
 * padre de un complemento de pago, así que una PPD que no timbra es un
 * complemento que nunca se va a poder registrar.
 *
 * Vive en el modelo y no en las pantallas porque el método se cambia en dos
 * lugares, el paso de pago y la edición en el mismo lugar de revisar. Aplicada
 * solo al armar el payload, la pantalla enseñaría 03 y el comprobante mandaría
 * 99.
 *
 * Al regresar de PPD a PUE se devuelve la transferencia: el 99 lo impuso el
 * método, no lo eligió el usuario, y con PUE ya se sabe cómo le pagaron.
 */
export const applyPaymentRules = (next: InvoiceDraft, previous?: InvoiceDraft): InvoiceDraft => {
  if (next.payment.method === 'PPD' && next.payment.form !== PPD_PAYMENT_FORM) {
    return { ...next, payment: { ...next.payment, form: PPD_PAYMENT_FORM } };
  }

  const leftPpd = previous?.payment.method === 'PPD' && next.payment.method === 'PUE';
  if (leftPpd && next.payment.form === PPD_PAYMENT_FORM) {
    return { ...next, payment: { ...next.payment, form: DEFAULT_PAYMENT_FORM } };
  }

  return next;
};

/**
 * Fecha de HOY del comprobante, resuelta en el MOMENTO de emitir y nunca
 * guardada en el borrador. (La retroactiva sí se guarda, porque la eligió el
 * usuario, y por eso caduca: ver `emissionDate.ts`.)
 *
 * Guardarla al crear el borrador parecía inocente y no lo es: el borrador se
 * persiste, así que uno abandonado el viernes y retomado el lunes timbraba con
 * la fecha del viernes, y pasadas 72 horas el SAT lo rechaza con CFDI40102.
 *
 * Va en UTC con `Z` y SIN corrección de huso: el backend la corre al huso del
 * `place_of_expedition`. Desplazarla aquí la movería dos veces, y en una
 * factura emitida cerca de medianoche eso cambia el día del comprobante.
 */
export const emissionDate = (): string => new Date().toISOString();

export const publicGeneralReceiver = (): DraftReceiver => ({
  mode: 'publico_general',
  name: 'Público en general',
  rfc: PUBLIC_RECEIVER_RFC,
  taxRegime: PUBLIC_RECEIVER_REGIME,
  postalCode: '',
});

export const foreignPublicReceiver = (): DraftReceiver => ({
  mode: 'publico_extranjero',
  name: 'Público en general en el extranjero',
  rfc: FOREIGN_RECEIVER_RFC,
  taxRegime: PUBLIC_RECEIVER_REGIME,
  postalCode: '',
});

export type GenericReceiverMode = Exclude<ReceiverMode, 'cliente'>;

/** Los receptores genéricos del SAT que se eligen con un clic, en el orden en que se ofrecen. */
export const GENERIC_RECEIVERS: ReadonlyArray<{
  mode: GenericReceiverMode;
  hint: string;
  build: () => DraftReceiver;
}> = [
  { mode: 'publico_general', hint: 'Sin datos del cliente', build: publicGeneralReceiver },
  { mode: 'publico_extranjero', hint: 'Clientes fuera de México', build: foreignPublicReceiver },
];

const grossUpInputOf = (concept: DraftConcept) => ({
  taxRate: ivaRateOf(concept.ivaTreatment),
  grossUp: concept.grossUp,
  retentions: concept.retentions,
});

/**
 * Importes del concepto en los términos del SAT.
 *
 * `total` es el Total del comprobante: ya descuenta lo retenido, así que es lo
 * que el emisor recibe.
 *
 * `subtotal` es la BASE del impuesto (importe menos descuento), no el Importe:
 * el SAT pide `Importe = Cantidad × ValorUnitario` ANTES del descuento y el
 * descuento aparte. Mandar la base como Importe y además el descuento lo
 * restaba dos veces, y el comprobante salía por debajo de lo que el usuario
 * revisó.
 *
 * Con gross-up el descuento se captura en los mismos términos que el precio
 * (lo que el emisor deja de recibir), así que también se lleva hacia atrás.
 *
 * Todo va AL CENTAVO, como viaja al comprobante: el Go no recalcula, escribe en el
 * XML lo que le mandamos y suma los conceptos ya redondeados. Sumar aquí los
 * importes crudos dejaba el resumen de la pantalla un centavo arriba o abajo del
 * Total del XML. Cada retención es `Base × TasaOCuota` sobre la Base ya
 * redondeada, igual que `ComputeConceptRetention` del Go, para que el Importe
 * cuadre con la Base y la tasa que el SAT lee en el mismo nodo.
 */
export interface ConceptAmounts extends ConceptTotals {
  importe: number;
  discount: number;
  retainedIsr: number;
  retainedIva: number;
}

/** Un cliente guardado como receptor del borrador. Lo usan el inicio y el cambio de receptor del paso 3. */
export const draftReceiverFromClient = (client: Receiver): DraftReceiver => ({
  mode: 'cliente',
  clientId: client.id,
  name: client.name,
  rfc: client.rfc,
  taxRegime: client.tax_regime,
  postalCode: client.postal_code,
  email: client.email ?? undefined,
  usoCfdi: client.usocfdi,
  residenciaFiscal: client.residencia_fiscal || undefined,
  numRegIdTrib: client.num_reg_id_trib || undefined,
});

/** Al centavo, con el mismo `toFixed` con el que el Go escribe los importes del XML. */
export const roundCents = (value: number): number => Number(value.toFixed(2));

export const conceptTotalsOf = (concept: DraftConcept): ConceptAmounts => {
  const base = calculateConceptTotals({
    unitPrice: concept.unitPrice,
    quantity: concept.quantity,
    discount: concept.discount,
    ...grossUpInputOf(concept),
  });
  const { subtotal: rawImporte } = calculateConceptTotals({
    unitPrice: concept.unitPrice,
    quantity: concept.quantity,
    discount: 0,
    ...grossUpInputOf(concept),
  });
  const importe = roundCents(rawImporte);
  const discount = roundCents(rawImporte - base.subtotal);
  const subtotal = roundCents(base.subtotal);
  const taxes = roundCents(base.taxes);
  const retainedIsr = roundCents(subtotal * (concept.retentions?.isrRate ?? 0));
  const retainedIva = roundCents(subtotal * (concept.retentions?.ivaRate ?? 0));
  return {
    importe,
    discount,
    subtotal,
    taxes,
    retainedIsr,
    retainedIva,
    total: roundCents(importe - discount + taxes - retainedIsr - retainedIva),
  };
};

/**
 * Precio unitario NETO del concepto: el que va al SAT como ValorUnitario y el
 * que ve el usuario en la revisión. Con gross-up no es el que capturó, y va
 * antes del descuento, igual que en el comprobante.
 */
export const conceptNetUnitPrice = (concept: DraftConcept): number =>
  conceptTotalsOf(concept).importe / Math.max(concept.quantity, 1);

/**
 * Qué guardar en `unitPrice` para que el concepto muestre ESE unitario neto.
 *
 * La edición in-place del paso 3 escribe sobre el precio que se ve, no sobre el
 * capturado: con gross-up teclear 100 y que el renglón muestre 86.21 se lee
 * como un error de la app. El descuento no entra: el unitario es previo a él.
 */
export const capturedUnitPriceForNet = (concept: DraftConcept, netUnitPrice: number): number =>
  unitPriceForSubtotal({
    subtotal: netUnitPrice * Math.max(concept.quantity, 1),
    quantity: concept.quantity,
    discount: 0,
    ...grossUpInputOf(concept),
  });

/**
 * Renglones de la tarjeta de importes. Con `concepts` desglosa cada concepto
 * con su cantidad; sin ellos, un solo subtotal. El descuento va aparte y en
 * negativo, igual que en el comprobante: sumado dentro del subtotal no se ve
 * de dónde sale el total.
 */
export const draftSummaryLines = (totals: ConceptAmounts, concepts?: DraftConcept[]) => [
  ...(concepts
    ? concepts.map((concept) => ({
        label: `${concept.quantity} × ${concept.description || 'Concepto'}`,
        amount: conceptTotalsOf(concept).importe,
      }))
    : [{ label: 'Subtotal', amount: totals.importe }]),
  ...(totals.discount > 0 ? [{ label: 'Descuento', amount: -totals.discount }] : []),
  { label: 'IVA', amount: totals.taxes },
  ...(totals.retainedIsr > 0 ? [{ label: 'ISR retenido', amount: -totals.retainedIsr }] : []),
  ...(totals.retainedIva > 0 ? [{ label: 'IVA retenido', amount: -totals.retainedIva }] : []),
];

/**
 * Destinatarios que van en `email.to`, ya resueltos.
 *
 * El backend arma la lista final con `receiver.email` MÁS `email.to`, así que
 * el front es el responsable de mandarla completa y sin repetir: una dirección
 * en los dos lados recibe la factura dos veces. El correo de la cuenta va
 * primero, igual que en el wizard actual, porque es el que dispara la plantilla
 * del emisor.
 */
export const draftCopyRecipients = (draft: InvoiceDraft): string[] => {
  const receiverEmail = draft.receiver?.email?.trim().toLowerCase();
  const candidates = [draft.issuer?.email, ...draft.emails];
  const seen = new Set<string>();

  return candidates.reduce<string[]>((list, raw) => {
    const email = raw?.trim();
    const key = email?.toLowerCase();
    if (!email || !key || key === receiverEmail || seen.has(key)) return list;
    seen.add(key);
    return [...list, email];
  }, []);
};

/** Suma de los conceptos ya al centavo, como el Total del XML: ver `ConceptAmounts`. */
export const draftTotals = (draft: InvoiceDraft): ConceptAmounts =>
  draft.concepts.map(conceptTotalsOf).reduce<ConceptAmounts>(
    (acc, t) => ({
      importe: roundCents(acc.importe + t.importe),
      discount: roundCents(acc.discount + t.discount),
      subtotal: roundCents(acc.subtotal + t.subtotal),
      taxes: roundCents(acc.taxes + t.taxes),
      retainedIsr: roundCents(acc.retainedIsr + t.retainedIsr),
      retainedIva: roundCents(acc.retainedIva + t.retainedIva),
      total: roundCents(acc.total + t.total),
    }),
    { importe: 0, discount: 0, subtotal: 0, taxes: 0, retainedIsr: 0, retainedIva: 0, total: 0 },
  );

/**
 * Público en general lo decide el RFC, no el modo de la UI.
 *
 * El modo solo se estampa cuando el usuario elige esa opción en el composer; un
 * cliente guardado o capturado a mano puede traer el RFC genérico igual, y ahí
 * el modo dice «cliente». Con el RFC genérico el SAT exige S01, régimen 616 y
 * que el domicilio del receptor sea el mismo lugar de expedición: tratarlo como
 * un cliente normal manda G03 y su propio CP, y el comprobante rebota.
 */
export const isPublicGeneralReceiver = (receiver?: DraftReceiver | null): boolean =>
  receiver?.mode === 'publico_general' ||
  receiver?.rfc?.trim().toUpperCase() === PUBLIC_RECEIVER_RFC;

/**
 * Los dos RFC genéricos del SAT, público en general y extranjero, exigen régimen
 * 616, uso S01 y un domicilio igual al lugar de expedición. Igual que con público
 * en general, lo decide el RFC: el backend también trae el genérico extranjero
 * entre sus receptores predeterminados, con un CP que el SAT rechaza.
 */
export const isGenericReceiver = (receiver?: DraftReceiver | null): boolean =>
  isPublicGeneralReceiver(receiver) || isForeignReceiver(receiver);

/** Un cliente en el extranjero, genérico o guardado con su nombre: lo decide el RFC `XEXX010101000`. */
export const isForeignReceiver = (receiver?: DraftReceiver | null): boolean =>
  receiver?.mode === 'publico_extranjero' ||
  receiver?.rfc?.trim().toUpperCase() === FOREIGN_RECEIVER_RFC;

/** El nodo InformacionGlobal, solo con Público en general y solo si el usuario eligió un periodo. */
export const globalInformationPayload = (
  receiver: DraftReceiver,
): { is_global?: true; global_information?: GlobalInformation } => {
  const info = isPublicGeneralReceiver(receiver) ? receiver.globalInformation : undefined;
  return info
    ? {
        is_global: true,
        global_information: { periodicity: info.periodicity, month: info.month, year: info.year },
      }
    : {};
};

/**
 * Un periodo que el SAT no acepta de este emisor: la bimestral solo es del RIF. Puede
 * quedar guardado si el emisor cambió de régimen después de elegirlo, o en un borrador
 * de antes de la regla. No se corrige en silencio: cambiarlo movería el periodo fiscal
 * de la factura sin que el usuario lo sepa, así que se pide que lo elija de nuevo.
 */
export const globalInformationProblem = (draft: InvoiceDraft): string | null => {
  const info = isPublicGeneralReceiver(draft.receiver) ? draft.receiver?.globalInformation : undefined;
  if (!info) return null;
  const allowed = periodicityOptionsFor(draft.issuer?.taxRegime).some(
    (option) => option.code === info.periodicity,
  );
  return allowed ? null : 'La periodicidad bimestral solo aplica a emisores del RIF. Elige otra.';
};

/** País y número fiscal del extranjero, solo con XEXX y solo los que se capturaron. */
export const foreignReceiverPayload = (
  receiver: DraftReceiver,
): { residencia_fiscal?: string; num_reg_id_trib?: string } => {
  if (!isForeignReceiver(receiver)) return {};
  const residenciaFiscal = normalizeForeignTaxId(receiver.residenciaFiscal);
  const numRegIdTrib = normalizeForeignTaxId(receiver.numRegIdTrib);
  return {
    ...(residenciaFiscal ? { residencia_fiscal: residenciaFiscal } : {}),
    ...(numRegIdTrib ? { num_reg_id_trib: numRegIdTrib } : {}),
  };
};

/** Qué corregir en los datos del extranjero antes de timbrar, o `null`. */
export const foreignReceiverProblem = (receiver?: DraftReceiver | null): string | null =>
  receiver && isForeignReceiver(receiver)
    ? foreignTaxIdProblem(receiver.residenciaFiscal, receiver.numRegIdTrib)
    : null;

/** Cinco dígitos: la forma de c_CodigoPostal. Que el CP exista en el catálogo lo valida el PAC. */
export const isValidPostalCode = (postalCode?: string | null): boolean =>
  /^\d{5}$/.test(postalCode?.trim() ?? '');

/** RFC, régimen y CP con los que el receptor llega al SAT. La vista previa y los dos payloads leen de aquí. */
export const receiverFiscalFields = (receiver: DraftReceiver, issuerPostalCode: string) => {
  const isGeneric = isGenericReceiver(receiver);
  return {
    rfc: isPublicGeneralReceiver(receiver) ? PUBLIC_RECEIVER_RFC : receiver.rfc,
    taxRegime: isGeneric ? PUBLIC_RECEIVER_REGIME : receiver.taxRegime,
    postalCode: isGeneric ? issuerPostalCode : receiver.postalCode,
  };
};

/**
 * Usos que el SAT reserva a OTROS tipos de comprobante: CP01 al complemento de
 * pago y CN01 a la nómina. Este flujo solo emite ingreso, así que nunca aplican.
 */
const USOS_FUERA_DE_INGRESO = new Set(['CP01', 'CN01']);

/** Los usos que el receptor puede elegir: los que su régimen admite en un ingreso. */
export const usoCfdiOptionsFor = (regime?: string | null): SatCatalogItem[] =>
  allowedUsosForRegime(regime).filter((uso) => !USOS_FUERA_DE_INGRESO.has(uso.code));

/**
 * El uso de CFDI lo elige el usuario, pero solo dentro de lo que el SAT admite.
 *
 * El RFC genérico no tiene elección: exige S01. Para los demás, la elección
 * guardada en el receptor manda mientras siga siendo válida para su régimen;
 * si no lo es (cambió el régimen, o viene de un cliente viejo con un uso que el
 * SAT ya no acepta) se cae al default del régimen en vez de mandar un rechazo
 * seguro. (El CP01 del complemento de pago no aparece porque este flujo no
 * emite P; ver `DraftInvoiceKind`.)
 */
export const resolveUsoCfdi = (draft: InvoiceDraft): string => {
  if (isGenericReceiver(draft.receiver)) return PUBLIC_RECEIVER_USO_CFDI;
  const regime = draft.receiver?.taxRegime;
  const chosen = draft.receiver?.usoCfdi;
  if (chosen && usoCfdiOptionsFor(regime).some((uso) => uso.code === chosen)) return chosen;
  return usoCfdiForRegime(regime);
};

/**
 * G03 no vale para todos los regímenes, y el default del catálogo es solo una
 * pista, así que se verifica antes de usarlo y se cae a S01, que acepta
 * cualquier régimen, antes que mandar un comprobante a un rechazo seguro.
 *
 * La tabla de validez sola no alcanza. El default para el régimen 605 (sueldos
 * y salarios) es CP01, y CP01 figura como válido para TODOS los regímenes,
 * porque lo es… en un complemento de pago. Sin este filtro, una factura de
 * ingreso a un asalariado salía con el uso de CFDI de un pago.
 */
export const usoCfdiForRegime = (regime?: string | null): string => {
  const preferred = regime ? getDefaultUsoCfdiForRegime(regime) : 'G03';
  const isAllowed =
    !USOS_FUERA_DE_INGRESO.has(preferred) &&
    allowedUsosForRegime(regime).some((uso) => uso.code === preferred);
  return isAllowed ? preferred : PUBLIC_RECEIVER_USO_CFDI;
};

export const usoCfdiName = (code: string): string =>
  stripCodePrefix(USOS_CFDI.find((uso) => uso.code === code)?.name ?? '');

/**
 * Lo que la pantalla de pago muestra sale de la MISMA función que arma el
 * payload. Escribir la clave a mano en la vista es como se llega a una
 * pantalla que promete S01 y un comprobante que manda G03.
 */
export const describeUsoCfdi = (draft: InvoiceDraft) => {
  const code = resolveUsoCfdi(draft);
  return { code, name: usoCfdiName(code) };
};

/**
 * Tasas de retención del régimen del emisor frente a un receptor, una por
 * actividad: el catálogo distingue servicio de venta de bienes (626 conserva
 * solo el ISR con bienes, 612 no retiene nada). `null` = esa actividad no
 * retiene. El IVA viene como la referencia de 2/3 del 16%.
 */
export interface RetentionRule {
  service: ConceptRetentionRates | null;
  goods: ConceptRetentionRates | null;
}

const REFERENCE_IVA_RATE = 0.16;

/**
 * Retención de UN concepto, mismas reglas que el wizard legacy: el ISR se
 * retiene sobre la base y sobrevive a exento y 0%; el IVA retenido escala a la
 * tasa real del concepto (2/3 del 8% en frontera) con 6 decimales, el tope del
 * SAT para TasaOCuota; sin objeto de impuesto no lleva ninguna.
 */
const conceptRetentionRates = (
  concept: DraftConcept,
  rule: RetentionRule,
): ConceptRetentionRates | undefined => {
  if (concept.ivaTreatment === 'no_objeto') return undefined;
  const rates = inferIsGoods(concept.satCode) ? rule.goods : rule.service;
  if (!rates) return undefined;
  const ivaRate = ivaRateOf(concept.ivaTreatment);
  const retainedIvaRate =
    ivaRate > 0 ? Math.round(rates.ivaRate * (ivaRate / REFERENCE_IVA_RATE) * 1e6) / 1e6 : 0;
  if (rates.isrRate <= 0 && retainedIvaRate <= 0) return undefined;
  return { isrRate: rates.isrRate, ivaRate: retainedIvaRate };
};

/** Para quién se decidieron `RetentionWaiver` y `RetentionRates`: régimen del emisor y RFC del receptor. */
export const retentionScope = (draft: Pick<InvoiceDraft, 'issuer' | 'receiver'>): string =>
  `${draft.issuer?.taxRegime ?? ''}|${(draft.receiver?.rfc ?? '').trim().toUpperCase()}`;

/** La excepción del borrador, solo si sigue siendo para este emisor y este receptor. */
export const activeRetentionWaiver = (draft: InvoiceDraft): RetentionWaiver | null => {
  const waiver = draft.retentionWaiver;
  if (!waiver || (!waiver.isr && !waiver.iva)) return null;
  return waiver.scope === retentionScope(draft) ? waiver : null;
};

/** Las tasas capturadas, solo si siguen siendo para este emisor y este receptor y si ese receptor retiene. */
export const activeRetentionRates = (draft: InvoiceDraft): RetentionRates | null => {
  const rates = draft.retentionRates;
  if (!rates || !receiverCanWithhold(draft.receiver?.rfc)) return null;
  return rates.scope === retentionScope(draft) ? rates : null;
};

/** Sin objeto de impuesto no se retiene nada, y el IVA solo se retiene donde se traslada. */
const conceptCarries = (concept: DraftConcept, tax: RetentionTax): boolean =>
  concept.ivaTreatment !== 'no_objeto' && (tax === 'isr' || ivaRateOf(concept.ivaTreatment) > 0);

/**
 * El borrador con las retenciones que le tocan. Se deriva en cada render: con un
 * receptor que no retiene (persona física, RFC genérico) los conceptos quedan sin
 * retenciones aunque las trajeran.
 *
 * Cada impuesto se resuelve en este orden:
 * 1. Quitado con «Mi cliente no me retiene», si la regla lo ponía: no se retiene.
 * 2. Sin tasa capturada: la de la regla, concepto por concepto.
 * 3. Con tasa capturada: esa tasa en los conceptos donde la regla pone el impuesto,
 *    porque cambiar la tasa no le agrega ISR a la venta de bienes de un 612. Si la
 *    regla no lo pone en ningún concepto, el usuario lo está agregando y va en todos
 *    los que pueden llevarlo.
 */
export const withRetentions = (draft: InvoiceDraft, rule: RetentionRule | null): InvoiceDraft => {
  const applicableRule = rule && receiverCanWithhold(draft.receiver?.rfc) ? rule : null;
  const byRule = draft.concepts.map((concept) =>
    applicableRule ? conceptRetentionRates(concept, applicableRule) : undefined,
  );
  const ruleRetains = (tax: RetentionTax) =>
    byRule.some((rates) => (rates?.[RATE_KEY[tax]] ?? 0) > 0);
  const ruleTaxes: Record<RetentionTax, boolean> = { isr: ruleRetains('isr'), iva: ruleRetains('iva') };
  const waiver = activeRetentionWaiver(draft);
  const captured = activeRetentionRates(draft);

  const rateOf = (tax: RetentionTax, concept: DraftConcept, ruleRate: number): number => {
    if (waiver?.[tax] && ruleTaxes[tax]) return 0;
    const capturedRate = captured?.[tax];
    if (capturedRate === undefined) return ruleRate;
    if (!capturedRate || !conceptCarries(concept, tax)) return 0;
    return ruleTaxes[tax] && ruleRate <= 0 ? 0 : capturedRate;
  };

  return {
    ...draft,
    concepts: draft.concepts.map((concept, index) => {
      const isrRate = rateOf('isr', concept, byRule[index]?.isrRate ?? 0);
      const ivaRate = rateOf('iva', concept, byRule[index]?.ivaRate ?? 0);
      return {
        ...concept,
        retentions: isrRate > 0 || ivaRate > 0 ? { isrRate, ivaRate } : undefined,
      };
    }),
  };
};

/**
 * La tasa que pone la regla en este borrador por impuesto, sin nada de lo que decidió
 * el usuario, o `null` si la regla no lo retiene. Es el default del editor; con
 * conceptos a distinta tasa de IVA, la del primero que lo lleva.
 */
export const ruleRatesOf = (
  draft: InvoiceDraft,
  rule: RetentionRule | null,
): Record<RetentionTax, number | null> => {
  const { concepts } = withRetentions(
    { ...draft, retentionWaiver: undefined, retentionRates: undefined },
    rule,
  );
  const firstRate = (tax: RetentionTax) =>
    concepts.map((concept) => conceptRetentionRateOf(concept, tax)).find((rate) => rate > 0) ?? null;
  return { isr: firstRate('isr'), iva: firstRate('iva') };
};

/**
 * Lo que de verdad se quitó: la excepción del usuario sobre lo que la regla ponía.
 * Quitar el IVA donde la regla no retiene IVA no es una excepción. `null` = ninguna.
 */
export const waivedRetentionsOf = (
  draft: InvoiceDraft,
  rule: RetentionRule | null,
): Record<RetentionTax, boolean> | null => {
  const waiver = activeRetentionWaiver(draft);
  if (!waiver) return null;
  const byRule = ruleRatesOf(draft, rule);
  const waived = { isr: waiver.isr && byRule.isr !== null, iva: waiver.iva && byRule.iva !== null };
  return waived.isr || waived.iva ? waived : null;
};

/**
 * Los impuestos que el borrador YA DERIVADO retiene a una tasa que capturó el usuario
 * y no la regla, o `null`. Es «retención capturada a mano» del lado del front.
 */
export const capturedRetentionsOf = (draft: InvoiceDraft): Record<RetentionTax, boolean> | null => {
  const captured = activeRetentionRates(draft);
  const isCaptured = (tax: RetentionTax) => {
    const rate = captured?.[tax];
    return !!rate && draft.concepts.some((concept) => conceptRetentionRateOf(concept, tax) === rate);
  };
  const result = { isr: isCaptured('isr'), iva: isCaptured('iva') };
  return result.isr || result.iva ? result : null;
};

/** «el ISR», «el IVA» o «el ISR y el IVA», para la advertencia de lo que se quitó. */
export const waivedTaxesPhrase = (waived: Record<RetentionTax, boolean>): string =>
  waived.isr && waived.iva ? 'el ISR y el IVA' : waived.isr ? 'el ISR' : 'el IVA';

/** 0.025 → 2.5, sin el ruido binario de multiplicar por 100. */
export const rateToPercent = (rate: number): number => Number((rate * 100).toFixed(4));

/** 2.5 → 0.025, con los 6 decimales que admite TasaOCuota. */
export const percentToRate = (percent: number): number => Number((percent / 100).toFixed(6));

/**
 * «ISR al 2.5% e IVA al 8%»: las tasas capturadas que sí se aplican, sobre el borrador
 * ya derivado, o `null`. La revisión las enseña porque un 25 tecleado por un 2.5 solo
 * se ve si la tasa está a la vista junto al importe.
 */
export const capturedRatesPhrase = (draft: InvoiceDraft): string | null => {
  const captured = capturedRetentionsOf(draft);
  const rates = activeRetentionRates(draft);
  if (!captured || !rates) return null;
  return RETENTION_TAXES.filter((tax) => captured[tax])
    .map((tax) => `${RETENTION_TAX_NAME[tax]} al ${rateToPercent(rates[tax] ?? 0)}%`)
    .join(' e ');
};

/**
 * Topes de c_TasaOCuota para una retención con TipoFactor Tasa: ISR de 0.000000 a
 * 0.350000 e IVA de 0.000000 a 0.160000. Fuera de ese rango el PAC rechaza el
 * comprobante (Anexo 20, nodo Concepto/Impuestos/Retenciones/Retencion).
 */
const MAX_RETAINED_RATE: Record<RetentionTax, number> = { isr: 0.35, iva: 0.16 };

/**
 * Qué está mal en las tasas capturadas, por impuesto, sobre el borrador ya derivado.
 * Cada frase completa un «Falta …»: el editor la pinta junto a su campo y
 * `missingRequiredFields` la usa para bloquear el timbrado. Nada se corrige en
 * silencio: una tasa mal capturada es una factura con el impuesto equivocado.
 *
 * El IVA retenido además no puede pasar del trasladado en el concepto (Art. 1-A
 * LIVA retiene el impuesto que se traslada): una tasa de 10.6667% sobre un concepto
 * de frontera al 8% retendría más IVA del que se cobró.
 */
export const retentionRateProblems = (
  draft: InvoiceDraft,
): Partial<Record<RetentionTax, string>> => {
  const captured = activeRetentionRates(draft);
  const problems: Partial<Record<RetentionTax, string>> = {};
  if (!captured) return problems;

  RETENTION_TAXES.forEach((tax) => {
    const rate = captured[tax];
    const name = RETENTION_TAX_NAME[tax];
    if (rate === undefined) return;
    if (!rate) {
      problems[tax] = `la tasa de ${name} que te retienen`;
      return;
    }
    if (rate > MAX_RETAINED_RATE[tax]) {
      problems[tax] = `una tasa de ${name} retenido de ${rateToPercent(MAX_RETAINED_RATE[tax])}% o menos`;
      return;
    }
    if (!draft.concepts.some((concept) => conceptRetentionRateOf(concept, tax) > 0)) {
      problems[tax] =
        tax === 'iva'
          ? 'un concepto con IVA para que te retengan IVA'
          : 'un concepto con impuestos para que te retengan ISR';
      return;
    }
    const overTransferred = draft.concepts.find(
      (concept) =>
        tax === 'iva' &&
        conceptRetentionRateOf(concept, 'iva') > ivaRateOf(concept.ivaTreatment),
    );
    if (overTransferred) {
      const transferred = rateToPercent(ivaRateOf(overTransferred.ivaTreatment));
      problems[tax] = `una tasa de IVA retenido de ${transferred}% o menos: no puede pasar del IVA de tu concepto`;
    }
  });
  return problems;
};

/**
 * Todo lo que el contrato de timbrado exige y el borrador todavía no tiene.
 *
 * Es la lista completa de campos REQUERIDOS, no una selección: cada uno de
 * estos sale al payload como cadena vacía o cero si nadie lo revisa, y el
 * schema del servicio los acepta (`""` es un string válido) para que después
 * los rechace el PAC. Un 422 mudo o un rechazo fiscal es mucho peor que un
 * mensaje que diga qué falta.
 *
 * Alimenta el mensaje del CTA y el candado del timbrado, nunca un `disabled`.
 */
export const missingRequiredFields = (draft: InvoiceDraft): string[] => {
  const missing: string[] = [];
  if (!draft.receiver) missing.push('el receptor');
  // `place_of_expedition` es el CP que eligió el emisor o, sin elección, el de su
  // perfil fiscal. Uno que no tiene la forma del catálogo lo rechaza el PAC.
  if (!isValidPostalCode(draft.issuer?.postalCode)) missing.push('el código postal desde donde facturas');
  if (!draft.issuer?.taxRegime) missing.push('el régimen con el que facturas');
  // El SAT exige el tipo de cambio en toda factura en moneda extranjera.
  if (draft.payment.currency !== 'MXN' && !draft.payment.exchangeRate) {
    missing.push('el tipo de cambio');
  }
  if (foreignReceiverProblem(draft.receiver)) {
    missing.push('el país o el número fiscal de tu cliente en el extranjero');
  }
  if (globalInformationProblem(draft)) {
    missing.push('otra periodicidad para la factura global: la bimestral es solo del RIF');
  }
  missing.push(...Object.values(retentionRateProblems(draft)));
  draft.concepts.forEach((concept, index) => {
    const position = draft.concepts.length > 1 ? ` del concepto ${index + 1}` : '';
    if (!concept.description.trim()) missing.push(`el producto o servicio${position}`);
    if (!concept.satCode.trim()) missing.push(`la clave del SAT${position}`);
    if (concept.unitPrice <= 0) missing.push(`el precio${position}`);
    if (concept.quantity < 1) missing.push(`la cantidad${position}`);
  });
  return missing;
};

export const isDraftReadyToStamp = (draft: InvoiceDraft): boolean =>
  missingRequiredFields(draft).length === 0;

/**
 * Un borrador nuevo con el receptor, los conceptos y el pago de otro. Ids
 * nuevos para que la emisión no reuse la llave de idempotencia de la factura
 * que se repite: con la misma llave, el backend devolvería la anterior.
 *
 * La fecha de emisión no se repite: la retroactiva era de aquella factura, y
 * esta empieza en «hoy, al timbrar». Las tasas capturadas sí: son las que ese
 * cliente retiene (ver `RetentionRates`).
 */
export const repeatDraft = (
  source: InvoiceDraft,
  issuer: DraftIssuer | null,
): InvoiceDraft => ({
  ...createEmptyDraft(issuer),
  // El periodo de una factura global es de esa factura: la siguiente lo vuelve a elegir.
  receiver: source.receiver && { ...source.receiver, globalInformation: undefined },
  concepts: source.concepts.map((concept) => ({ ...concept, id: crypto.randomUUID() })),
  payment: source.payment,
  emails: source.emails,
  retentionRates: source.retentionRates,
});

/** Resumen corto para la tarjeta de recuperación del home. */
export const draftSummaryLine = (draft: InvoiceDraft): string => {
  const receiver = draft.receiver ? displayFiscalName(draft.receiver.name) : 'Sin receptor';
  const total = draftTotals(draft).total;
  return `${receiver} · ${formatDraftAmount(total)}`;
};

export const formatDraftAmount = (amount: number, currency = 'MXN'): string =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
